import type { ReactNode } from 'react';
import { Icon } from '../ui/icons';

/**
 * The dashboard and Studio are for computers and the NoorView app only. Phone and tablet browsers get a message
 * instead. (TVs never come here: they use /display, which isn't gated.) The NoorView app identifies itself with
 * "NoorViewApp" in its user agent, so it's always allowed.
 *
 * This keeps casual phone-browser use out; the real protection is that every edit needs a signed-in paid account.
 */
export function editingBlocked(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent || '';
  if (/NoorViewApp\//.test(ua)) return false;
  const uaData = (nav as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile) return true;
  if (/Android|iPhone|iPod|iPad|Mobile|Silk|Kindle|Windows Phone|IEMobile|Opera Mini/i.test(ua)) return true;
  // iPadOS reports itself as a Mac; a touch screen gives it away.
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return true;
  return false;
}

/**
 * Opened from a reset or invite email (or the address still remembers one): the person needs to choose a password,
 * which works on any phone, so that step isn't blocked. They then sign in in the NoorView app.
 */
export function fromPasswordLink(): boolean {
  try {
    const p = new URLSearchParams(`${location.search.slice(1)}&${location.hash.replace(/^#/, '')}`);
    const type = p.get('type');
    if (p.get('access_token') && (type === 'recovery' || type === 'invite')) return true;
    if (p.get('error_description') || p.get('error')) return true;
    return sessionStorage.getItem('noorview.setPassword') === '1';
  } catch { return false; }
}

export function DeviceGate({ children, passwordLinks = false }: { children: ReactNode; passwordLinks?: boolean }) {
  if (!editingBlocked() || (passwordLinks && fromPasswordLink())) return <>{children}</>;
  return (
    <div className="gate-page">
      <main className="gate-card">
        <img className="gate-logo" src="/noorview-logo.png" alt="NoorView" width={64} height={64} />
        <h1>Open NoorView on a computer</h1>
        <p className="gate-lead">The dashboard and Studio can’t be used in a phone or tablet browser.</p>
        <ul className="gate-list">
          <li><span className="gate-ic"><Icon name="laptop" size={20} /></span><span><b>On a computer</b><small>Windows or Mac, in any web browser</small></span></li>
          <li><span className="gate-ic"><Icon name="phone" size={20} /></span><span><b>NoorView app</b><small>iPhone, iPad and Android. Coming soon.</small></span></li>
          <li><span className="gate-ic"><Icon name="tv" size={20} /></span><span><b>Setting up a screen?</b><small>Open <span className="gate-url">{location.host}/display</span> on the TV</small></span></li>
        </ul>
      </main>
    </div>
  );
}
